/**
 * frameQuality — pure client-side scorer for live capture quality.
 *
 * Inputs a <video> or <img>/canvas, samples a centred ROI, computes:
 *   • brightness        — mean luma 0..255
 *   • sharpness         — Laplacian-like edge density (approximation)
 *   • exposure penalty  — over/under-bright pages tank the score
 *   • hindrance         — v1.1: skin-tone or lens-block obstruction detector
 *
 * Returns { score 0..100, level: 'red'|'amber'|'green', brightness,
 *           sharpness, hindrance: { present, kind, coverage } }.
 *
 * We keep the math cheap so it can run at ~10Hz on a mid-tier phone
 * without dropping recording fps.
 */

const ROI_FRACTION = 0.6;     // sample the centre 60% × 60% of the frame
const DOWNSCALE = 96;         // ROI is resized to 96×96 before scoring

// v1.1 hindrance thresholds — tuned empirically on printed page samples.
// A frame is deemed "hindered" when a suspect blob covers a large slice
// of the ROI. `hand` = skin-tone mask; `lens` = very dark uniform mask.
const HINDRANCE_HAND_COVERAGE = 0.22;
const HINDRANCE_LENS_COVERAGE = 0.55;
const HINDRANCE_LENS_LUMA_MAX = 24;   // near-black

let _scratchCanvas = null;
let _scratchCtx = null;
function _scratch() {
  if (!_scratchCanvas) {
    _scratchCanvas = document.createElement('canvas');
    _scratchCanvas.width = DOWNSCALE;
    _scratchCanvas.height = DOWNSCALE;
    _scratchCtx = _scratchCanvas.getContext('2d', { willReadFrequently: true });
  }
  return _scratchCtx;
}

export function scoreFrame(videoEl) {
  if (!videoEl || videoEl.readyState < 2) {
    return { ok: false, score: 0, level: 'red', reason: 'not-ready' };
  }
  const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
  if (!vw || !vh) return { ok: false, score: 0, level: 'red', reason: 'no-frame' };

  const sw = Math.floor(vw * ROI_FRACTION);
  const sh = Math.floor(vh * ROI_FRACTION);
  const sx = Math.floor((vw - sw) / 2);
  const sy = Math.floor((vh - sh) / 2);

  const ctx = _scratch();
  try {
    ctx.drawImage(videoEl, sx, sy, sw, sh, 0, 0, DOWNSCALE, DOWNSCALE);
  } catch {
    return { ok: false, score: 0, level: 'red', reason: 'draw-failed' };
  }
  const { data } = ctx.getImageData(0, 0, DOWNSCALE, DOWNSCALE);

  // Pass 1 — luma map + global brightness + hindrance counters
  const N = DOWNSCALE * DOWNSCALE;
  const luma = new Float32Array(N);
  let sum = 0;
  let skinPx = 0;   // v1.1 — pixels that look like human skin (hand/finger obstruction)
  let darkPx = 0;   // v1.1 — pixels near-black (lens covered / sleeve / thumb on lens)
  for (let i = 0, j = 0; i < data.length; i += 4, j += 1) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    // Rec.709 luma
    const y = r * 0.2126 + g * 0.7152 + b * 0.0722;
    luma[j] = y;
    sum += y;
    // v1.1 — cheap RGB skin-tone heuristic (Kovac-style rule, works across
    // most skin tones because we only need "is there a big blob of hand
    // covering the page", not fine-grained biometrics).
    if (
      r > 70 && g > 35 && b > 20
      && r > g && r > b
      && (Math.max(r, g, b) - Math.min(r, g, b)) > 12
      && Math.abs(r - g) > 12
    ) {
      skinPx += 1;
    }
    if (y <= HINDRANCE_LENS_LUMA_MAX) darkPx += 1;
  }
  const brightness = sum / N;          // 0..255
  const skinCoverage = skinPx / N;     // 0..1
  const darkCoverage = darkPx / N;     // 0..1
  const hindrance = _classifyHindrance(skinCoverage, darkCoverage);

  // Pass 2 — Laplacian-ish edge density (4-neighbour) for sharpness proxy
  let edgeSum = 0;
  let edgeCount = 0;
  for (let y = 1; y < DOWNSCALE - 1; y++) {
    for (let x = 1; x < DOWNSCALE - 1; x++) {
      const idx = y * DOWNSCALE + x;
      const c = luma[idx];
      const v = 4 * c - luma[idx - 1] - luma[idx + 1] - luma[idx - DOWNSCALE] - luma[idx + DOWNSCALE];
      edgeSum += v * v;
      edgeCount += 1;
    }
  }
  const sharpness = Math.sqrt(edgeSum / Math.max(1, edgeCount));  // 0..~250
  // Normalise empirically: <8 = blurry · 12-25 = ok · 25+ = crisp print
  const sharpScore = Math.max(0, Math.min(100, (sharpness - 8) * 5));

  // Brightness scoring — penalise <60 (dark) and >215 (washed out)
  let brightScore = 0;
  if (brightness < 60) brightScore = (brightness / 60) * 70;
  else if (brightness > 215) brightScore = Math.max(0, 70 - (brightness - 215) * 2);
  else if (brightness < 100) brightScore = 70 + ((brightness - 60) / 40) * 30; // 70→100
  else if (brightness > 180) brightScore = 100 - ((brightness - 180) / 35) * 30; // 100→70
  else brightScore = 100;

  // Final blended score (sharpness dominates — readability matters most)
  let score = Math.round(sharpScore * 0.65 + brightScore * 0.35);
  // v1.1 — hindrance tanks the score so the ring goes red and recording
  // stalls if the teacher is blocking the page. This also feeds Session
  // Health™ aggregation on the backend via the per-frame quality metric.
  if (hindrance.present) score = Math.min(score, hindrance.kind === 'lens' ? 15 : 30);

  let level = 'red';
  if (score >= 65) level = 'green';
  else if (score >= 38) level = 'amber';

  return {
    ok: true,
    score,
    level,
    brightness: Math.round(brightness),
    sharpness: Math.round(sharpness * 10) / 10,
    hindrance,   // v1.1 — { present, kind, coverage }
  };
}

// v1.1 — classify hindrance from skin-tone / dark-coverage counters.
function _classifyHindrance(skinCoverage, darkCoverage) {
  if (darkCoverage >= HINDRANCE_LENS_COVERAGE) {
    return { present: true, kind: 'lens', coverage: Math.round(darkCoverage * 100) / 100 };
  }
  if (skinCoverage >= HINDRANCE_HAND_COVERAGE) {
    return { present: true, kind: 'hand', coverage: Math.round(skinCoverage * 100) / 100 };
  }
  return { present: false, kind: 'none', coverage: 0 };
}

/** Capture the current video frame as a base64 JPEG. */
export function snapshotJPEG(videoEl, { maxWidth = 1280, quality = 0.82 } = {}) {
  if (!videoEl || videoEl.readyState < 2) return null;
  const vw = videoEl.videoWidth, vh = videoEl.videoHeight;
  if (!vw || !vh) return null;
  const scale = Math.min(1, maxWidth / Math.max(vw, vh));
  const w = Math.round(vw * scale), h = Math.round(vh * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  ctx.drawImage(videoEl, 0, 0, w, h);
  try {
    return c.toDataURL('image/jpeg', quality);
  } catch {
    return null;
  }
}
